<template>
  <div>
    <v-dialog
      v-model="dialog"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <template #activator="{ on }">
        <div class="list-item">
          <v-btn
            class="list-item-btn"
            :ripple="false"
            block
            color="white"
            v-on="on"
            @click="sendGADataLayer('remove-advisor')"
          >
            <div class="list-item-text">
              <span class="list-item-name">{{ listItemName }}</span>
            </div>
          </v-btn>
        </div>
      </template>

      <v-card class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11">
              <h2 class="text-h5">{{ heading }}</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider />
        <v-card-text class="crud-card-text">
          <ValidationObserver ref="observer">
            <div v-if="!advisorExists">
              <ValidationProvider
                id="advisor_email"
                v-slot="{ errors }"
                name="advisor_email"
                rules="required|email"
              >
                <v-text-field
                  v-model.trim="email"
                  label="Advisor email"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </div>
          </ValidationObserver>
        </v-card-text>

        <v-card-actions class="crud-card-actions">
          <v-btn
            rounded
            depressed
            color="primary"
            :loading="buttonLoading"
            @click="submit()"
          >
            <span>{{ buttonText }}</span>
            <v-icon small>{{ icons.mdiArrowRight }}</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />
  </div>
</template>

<script>
  import {
    ASSIGN_ADVISOR_MUTATION,
    REMOVE_ADVISOR_MUTATION,
  } from '../../constants/graphql/advisors';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import SuccessSnackbar from '../common/SuccessSnackbar.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';

  export default {
    name: 'InviteAdvisor',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        email: null,
        showSuccess: false,
        successMessage: null,
        dialog: false,
        errorMessage: null,
      };
    },
    computed: {
      listItemName() {
        return this.advisorExists
          ? this.testator.advisor.fullName
          : 'Invite an advisor';
      },
      advisorExists() {
        return this.testator.advisor;
      },
      buttonText() {
        return this.advisorExists
          ? 'Remove ' + this.testator.advisor.fullName + ' as an advisor'
          : 'Invite advisor';
      },
      heading() {
        return this.advisorExists ? 'Remove advisor' : 'Invite an advisor';
      },
    },
    methods: {
      sendGADataLayer(event) {
        if (
          this.buttonText ===
          'Remove ' + this.testator.advisor?.fullName + ' as an advisor'
        ) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: event,
            omuserid: this.$store.getters?.userId,
          });
        }
      },
      submit() {
        if (
          this.buttonText ===
          'Remove ' + this.testator.advisor?.fullName + ' as an advisor'
        ) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: `remove-${this.testator.advisor?.fullName
              ?.trim()
              ?.toLowerCase()
              ?.replace(/\s+/g, '-')}-as-an-advisor`,
            omuserid: this.$store.getters?.userId,
          });
        }
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.advisorExists ? this.removeAdvisor() : this.assignAdvisor();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      removeAdvisor() {
        const testatorId = this.testator.id;
        const sendEmailToAdvisor = true;

        this.$apollo
          .mutate({
            mutation: REMOVE_ADVISOR_MUTATION,
            variables: {
              testatorId,
              sendEmailToAdvisor,
            },
            update: (cache, { data: { removeAdvisor } }) => {
              this.updateCacheAfterRemoveAdvisor(cache, removeAdvisor.testator);
            },
          })
          .then(_data => {
            this.successMessage = 'Advisor successfully removed';
            this.showSuccessMessage();
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateCacheAfterRemoveAdvisor(cache, testator) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        data.testator.advisorId = testator.advisorId;
        data.testator.advisor = testator.advisor;
        if (
          !this.$store.getters.isAdvisor &&
          !this.$store.getters.isCompanyAdmin
        ) {
          data.testator.company = null;
        }

        cache.writeQuery({ ...query, data: data });
      },
      assignAdvisor() {
        const testatorId = this.testator.id;
        const email = this.email;

        this.$apollo
          .mutate({
            mutation: ASSIGN_ADVISOR_MUTATION,
            variables: {
              testatorId,
              email,
            },
            update: (cache, { data: { assignAdvisor } }) => {
              this.updateCacheAfterAssignAdvisor(cache, assignAdvisor);
            },
          })
          .then(response => {
            this.successMessage = 'Advisor successfully added';
            this.showSuccessMessage();
          })
          .catch(error => {
            if (error.message.includes('GraphQL error:')) {
              this.errorMessage = error.message
                .replace('GraphQL error:', '')
                .trim();
              this.displayError();
            } else {
              this.displayError();
              throw error;
            }
          });
      },
      updateCacheAfterAssignAdvisor(cache, assignAdvisor) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        data.testator.advisorId = assignAdvisor.id;
        data.testator.advisor = assignAdvisor;
        if (
          !this.$store.getters.isAdvisor &&
          !this.$store.getters.isCompanyAdmin
        ) {
          data.testator.company = assignAdvisor.company;
        }

        cache.writeQuery({ ...query, data: data });
      },
      showSuccessMessage() {
        this.showSuccess = true;
        this.closeDialog();
      },
      closeDialog() {
        this.dialog = false;
        this.buttonLoading = false;
      },
    },
  };
</script>
